import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import { formatChartDay } from '@/lib/datetime';
import { Select, type SelectOption } from '@/components/Select';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { getOffers } from '@/api/offers';
import { getTrackerStats } from '@/api/tracker-stats';
import { useAuthStore } from '@/store/auth';
import { getAppGeoPerfRows, type AppGeoPerfRow } from '@/api/perf';
import type { Offer } from '@/api/types';
import { COUNTRY_MAP } from '@/lib/countries';
import {
  appAgeAnchor, appCohort, appCohortCutoff, groupAppGeoRowsByApp, type AppCohort,
} from '@/lib/app-performance';
import {
  groupStatsByManager, managerOf, ratePointDelta, relativeDelta, type ManagerRow, type ManagerTotals,
} from '@/lib/manager-performance';

function fmtCents(c: number): string {
  return `$${(c / 100).toFixed(2)}`;
}
function pct(num: number, denom: number): string {
  if (!denom) return '0.00%';
  return `${(num / denom * 100).toFixed(2)}%`;
}

type PerfMode = 'offer' | 'partner' | 'manager';
type DateBasis = 'click' | 'conversion';
type AppGeoSort = 'payout' | 'deps' | 'regs' | 'epc' | 'reg_cr' | 'dep_cr';
type PerformanceView = 'offers' | 'apps';
/** Table cohort filter: the two cohorts, plus 'all' to show them together. */
type AppCohortFilter = AppCohort | 'all';

interface RatioPoint {
  date: string;
  ratio: number | null;
  regs: number;
  deps: number;
}

function RatioTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload as RatioPoint;
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-content">{formatChartDay(p.date, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
      <div className="text-content-muted">Reg→Dep: <span className="font-medium text-content">{p.ratio === null ? '—' : `${p.ratio}%`}</span></div>
      <div className="text-content-muted">Deps: <span className="font-medium text-content">{p.deps}</span> · Regs: <span className="font-medium text-content">{p.regs}</span></div>
    </div>
  );
}

function DepRegRatioChart({ data }: { data: RatioPoint[] }) {
  const c = useThemeColors();
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            stroke={c.axis}
            tickFormatter={(d) => formatChartDay(d)}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke={c.axis}
            width={44}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<RatioTooltip />} />
          <Line
            type="monotone"
            dataKey="ratio"
            stroke={c.line}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const APP_GEO_SORT_OPTIONS: SelectOption[] = [
  { value: 'payout', label: 'Payout' },
  { value: 'deps', label: 'Deposits' },
  { value: 'regs', label: 'Registrations' },
  { value: 'epc', label: 'EPC' },
  { value: 'reg_cr', label: 'Registration CR' },
  { value: 'dep_cr', label: 'Reg → Dep CR' },
];

function appGeoSortValue(row: AppGeoPerfRow, sort: AppGeoSort): number {
  switch (sort) {
    case 'deps': return row.deps;
    case 'regs': return row.regs;
    case 'epc': return row.clicks > 0 ? row.payoutCents / row.clicks : 0;
    case 'reg_cr': return row.clicks > 0 ? row.regs / row.clicks : 0;
    case 'dep_cr': return row.regs > 0 ? row.deps / row.regs : 0;
    default: return row.payoutCents;
  }
}

function AppGeoTable({ rows, isLoading }: { rows: AppGeoPerfRow[]; isLoading: boolean }) {
  const totals = rows.reduce((acc, row) => ({
    clicks: acc.clicks + row.clicks,
    regs: acc.regs + row.regs,
    deps: acc.deps + row.deps,
    payoutCents: acc.payoutCents + row.payoutCents,
  }), { clicks: 0, regs: 0, deps: 0, payoutCents: 0 });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-muted">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-content-subtle">App</th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-content-subtle">Age date</th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-content-subtle">GEO</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Clicks</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Regs (CR)</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Deps (CR)</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Reg→Dep</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Payout</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">EPC</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-sm">
          {isLoading && <tr><td colSpan={9} className="py-6 text-center text-content-subtle">Loading…</td></tr>}
          {!isLoading && rows.length === 0 && (
            <tr><td colSpan={9} className="py-6 text-center text-content-subtle">No app × GEO data for this cohort</td></tr>
          )}
          {rows.map((row) => {
            const anchor = appAgeAnchor(row);
            return (
              <tr key={`${row.appId}|${row.country}`} className="hover:bg-surface-muted/50">
                <td className="px-3 py-2">
                  <div className="font-medium text-content">{row.appName}</div>
                  <div className="text-xs text-content-subtle">{row.bundleId}</div>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="text-content-muted">{formatChartDay(anchor.date, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  <div className="text-xs text-content-subtle">{anchor.source === 'released' ? 'Released' : 'Created'}</div>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {row.country === 'ALL' ? (
                    <span className="font-medium text-content">All GEOs</span>
                  ) : (
                    <>
                      <span className="font-medium text-content">{row.country}</span>
                      <span className="ml-1 text-xs text-content-subtle">{COUNTRY_MAP[row.country]?.name || (row.country === '??' ? 'Unknown' : row.country)}</span>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{row.clicks}</td>
                <td className="px-3 py-2 text-right">{row.regs} <span className="text-xs text-content-subtle">({pct(row.regs, row.clicks)})</span></td>
                <td className="px-3 py-2 text-right">{row.deps} <span className="text-xs text-content-subtle">({pct(row.deps, row.clicks)})</span></td>
                <td className="px-3 py-2 text-right text-content-muted">{pct(row.deps, row.regs)}</td>
                <td className="px-3 py-2 text-right">{fmtCents(row.payoutCents)}</td>
                <td className="px-3 py-2 text-right">{row.clicks > 0 ? `$${(row.payoutCents / 100 / row.clicks).toFixed(3)}` : '—'}</td>
              </tr>
            );
          })}
          {!isLoading && rows.length > 0 && (
            <tr className="bg-surface-muted font-medium">
              <td className="px-3 py-2" colSpan={3}>Total</td>
              <td className="px-3 py-2 text-right">{totals.clicks}</td>
              <td className="px-3 py-2 text-right">{totals.regs} <span className="text-xs text-content-subtle">({pct(totals.regs, totals.clicks)})</span></td>
              <td className="px-3 py-2 text-right">{totals.deps} <span className="text-xs text-content-subtle">({pct(totals.deps, totals.clicks)})</span></td>
              <td className="px-3 py-2 text-right text-content-muted">{pct(totals.deps, totals.regs)}</td>
              <td className="px-3 py-2 text-right">{fmtCents(totals.payoutCents)}</td>
              <td className="px-3 py-2 text-right">{totals.clicks > 0 ? `$${(totals.payoutCents / 100 / totals.clicks).toFixed(3)}` : '—'}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Delta({ value, unit }: { value: number | null; unit: 'pp' | '%' }) {
  if (value === null) return <span className="text-content-subtle">—</span>;
  const shown = unit === 'pp' ? value : value * 100;
  const tone = Math.abs(shown) < 0.05 ? 'text-content-muted' : shown > 0 ? 'text-success' : 'text-danger';
  return <span className={tone}>{shown > 0 ? '+' : ''}{shown.toFixed(1)}{unit}</span>;
}

function ManagerTable({
  managers, noOffer, total, isLoading, onSelect,
}: {
  managers: ManagerRow[];
  noOffer: ManagerTotals | null;
  total: ManagerTotals;
  isLoading: boolean;
  onSelect: (key: string) => void;
}) {
  const cols = 11;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-muted">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-content-subtle">Manager</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle" title="Offers created all time · created in the period · with clicks in the period">Offers</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Clicks</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Regs (CR)</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Deps (CR)</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Reg→Dep</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">Payout</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle">EPC</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle" title="Deposit CR (deps / clicks) minus everyone else's, percentage points">Dep CR vs others</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle" title="Reg→Dep minus everyone else's, percentage points">Reg→Dep vs others</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-content-subtle" title="EPC relative to everyone else's EPC">EPC vs others</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-sm">
          {isLoading && <tr><td colSpan={cols} className="py-6 text-center text-content-subtle">Loading…</td></tr>}
          {!isLoading && managers.length === 0 && (
            <tr><td colSpan={cols} className="py-6 text-center text-content-subtle">No offers yet</td></tr>
          )}
          {managers.map((m) => (
            <tr key={m.key} className="cursor-pointer hover:bg-surface-muted/50" onClick={() => onSelect(m.key)} title="Show this manager's offers">
              <td className="px-3 py-2">
                <div className="font-medium text-content">{m.name}</div>
                {m.email && m.email !== m.name && <div className="text-xs text-content-subtle">{m.email}</div>}
              </td>
              <td className="px-3 py-2 text-right">
                <div>{m.offers}</div>
                <div className="text-xs text-content-subtle">+{m.offersInPeriod} new · {m.offersWithTraffic} with traffic</div>
              </td>
              <td className="px-3 py-2 text-right">{m.clicks}</td>
              <td className="px-3 py-2 text-right">{m.regs} <span className="text-xs text-content-subtle">({pct(m.regs, m.clicks)})</span></td>
              <td className="px-3 py-2 text-right">{m.deps} <span className="text-xs text-content-subtle">({pct(m.deps, m.clicks)})</span></td>
              <td className="px-3 py-2 text-right text-xs text-content-muted">{pct(m.deps, m.regs)}</td>
              <td className="px-3 py-2 text-right">{fmtCents(m.payout_cents)}</td>
              <td className="px-3 py-2 text-right">{m.clicks ? `$${(m.payout_cents / 100 / m.clicks).toFixed(3)}` : '-'}</td>
              <td className="px-3 py-2 text-right"><Delta value={ratePointDelta(m.deps, m.clicks, m.others.deps, m.others.clicks)} unit="pp" /></td>
              <td className="px-3 py-2 text-right"><Delta value={ratePointDelta(m.deps, m.regs, m.others.deps, m.others.regs)} unit="pp" /></td>
              <td className="px-3 py-2 text-right"><Delta value={relativeDelta(m.payout_cents, m.clicks, m.others.payout_cents, m.others.clicks)} unit="%" /></td>
            </tr>
          ))}
          {!isLoading && noOffer && (
            <tr>
              <td className="px-3 py-2">
                <div className="font-medium italic text-content-subtle">No offer matched</div>
                <div className="text-xs text-content-subtle">fallback / no flow / deleted offer</div>
              </td>
              <td className="px-3 py-2 text-right">—</td>
              <td className="px-3 py-2 text-right">{noOffer.clicks}</td>
              <td className="px-3 py-2 text-right">{noOffer.regs} <span className="text-xs text-content-subtle">({pct(noOffer.regs, noOffer.clicks)})</span></td>
              <td className="px-3 py-2 text-right">{noOffer.deps} <span className="text-xs text-content-subtle">({pct(noOffer.deps, noOffer.clicks)})</span></td>
              <td className="px-3 py-2 text-right text-xs text-content-muted">{pct(noOffer.deps, noOffer.regs)}</td>
              <td className="px-3 py-2 text-right">{fmtCents(noOffer.payout_cents)}</td>
              <td className="px-3 py-2 text-right">{noOffer.clicks ? `$${(noOffer.payout_cents / 100 / noOffer.clicks).toFixed(3)}` : '-'}</td>
              <td className="px-3 py-2 text-right" colSpan={3} />
            </tr>
          )}
          {!isLoading && managers.length > 0 && (
            <tr className="bg-surface-muted font-medium">
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right">{managers.reduce((n, m) => n + m.offers, 0)}</td>
              <td className="px-3 py-2 text-right">{total.clicks}</td>
              <td className="px-3 py-2 text-right">{total.regs} <span className="text-xs text-content-subtle">({pct(total.regs, total.clicks)})</span></td>
              <td className="px-3 py-2 text-right">{total.deps} <span className="text-xs text-content-subtle">({pct(total.deps, total.clicks)})</span></td>
              <td className="px-3 py-2 text-right text-xs text-content-muted">{pct(total.deps, total.regs)}</td>
              <td className="px-3 py-2 text-right">{fmtCents(total.payout_cents)}</td>
              <td className="px-3 py-2 text-right">{total.clicks ? `$${(total.payout_cents / 100 / total.clicks).toFixed(3)}` : '-'}</td>
              <td className="px-3 py-2 text-right" colSpan={3} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function OfferPerformance() {
  const { data: offers = [] } = useQuery({ queryKey: ['offers'], queryFn: getOffers });

  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [mode, setMode] = useState<PerfMode>('offer');
  const [selectedId, setSelectedId] = useState<string>('');
  const [countries, setCountries] = useState<string[]>([]);
  // 'click' is the click cohort (a click's deps count on the click's date, even
  // if they land weeks later); 'conversion' counts regs/deps on the day the
  // postback arrived. Clicks/unique always sit on the click's own date.
  const [dateBasis, setDateBasis] = useState<DateBasis>('click');
  const [performanceView, setPerformanceView] = useState<PerformanceView>('offers');
  const isClient = useAuthStore((st) => st.isClient());
  const [appCohortMode, setAppCohortMode] = useState<AppCohortFilter>('new');
  const [appSearch, setAppSearch] = useState('');
  const [newAppWindowDays, setNewAppWindowDays] = useState(30);
  const [appGeoSort, setAppGeoSort] = useState<AppGeoSort>('payout');
  const [groupAppGeos, setGroupAppGeos] = useState(false);

  const coveredGeos = useMemo(() => {
    const set = new Set<string>();
    offers.forEach((o) => {
      (o.geo || '').split(',').forEach((c) => {
        const code = c.trim().toUpperCase();
        if (code) set.add(code);
      });
    });
    return Array.from(set).sort((a, b) =>
      (COUNTRY_MAP[a]?.name || a).localeCompare(COUNTRY_MAP[b]?.name || b));
  }, [offers]);

  // The focus selector scopes the whole page (table + chart), not just the chart.
  // Manager mode has no server-side filter: offers are attributed to their
  // creator on the client, so the request always stays per-offer and unscoped.
  const focusFilter = selectedId && mode !== 'manager'
    ? (mode === 'partner' ? { partner: selectedId } : { offer_id: selectedId })
    : {};

  // Picking a single advertiser breaks the table down into that advertiser's
  // offers — a one-row table would say nothing the chart doesn't already show.
  const tableGroup: 'offer' | 'partner' = mode === 'offer' || mode === 'manager' || selectedId ? 'offer' : 'partner';

  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['offer-perf', tableGroup, from, to, countries, mode === 'manager' ? '' : selectedId, dateBasis],
    queryFn: () => getTrackerStats({
      from: `${from} 00:00:00`,
      to: `${to} 23:59:59`,
      group_by: tableGroup,
      countries,
      date_basis: dateBasis,
      ...focusFilter,
    }),
    enabled: performanceView === 'offers',
  });

  const offerById = useMemo(() => {
    const m = new Map<string, Offer>();
    offers.forEach((o) => m.set(o.id, o));
    return m;
  }, [offers]);

  const managerStats = useMemo(
    () => groupStatsByManager(allRows, offers, { from, to }),
    [allRows, offers, from, to],
  );

  // A selected manager narrows the per-offer table to that person's offers.
  const rows = useMemo(() => {
    if (mode !== 'manager' || !selectedId) return allRows;
    return allRows.filter((r) => {
      const o = r.group_value ? offerById.get(r.group_value) : undefined;
      return o ? managerOf(o).key === selectedId : false;
    });
  }, [allRows, mode, selectedId, offerById]);

  // Options for the "focus" selector: a single offer or advertiser to scope the
  // table and chart to. First entry is the "all" reset.
  const focusOptions = useMemo<SelectOption[]>(() => {
    if (mode === 'manager') {
      const opts = managerStats.managers.map((m) => ({ value: m.key, label: m.email && m.email !== m.name ? `${m.name} · ${m.email}` : m.name }));
      return [{ value: '', label: 'All managers' }, ...opts];
    }
    if (mode === 'partner') {
      const set = new Set<string>();
      offers.forEach((o) => { if (o.partner) set.add(o.partner); });
      const opts = Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ value: p, label: p }));
      return [{ value: '', label: 'All advertisers' }, ...opts];
    }
    const opts = offers
      .map((o) => {
        const extra = [o.geo, o.brand, o.lang].filter(Boolean).join(' · ');
        return { value: o.id, label: extra ? `${o.partner} · ${extra}` : o.partner };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: '', label: 'All offers' }, ...opts];
  }, [offers, mode, managerStats]);

  // Daily deposit/registration ratio series for the trend chart. Grouped by day,
  // respecting the same date range + GEO filter as the table, plus an optional
  // single-offer / single-advertiser focus.
  const { data: dailyRows = [], isLoading: dailyLoading } = useQuery({
    queryKey: ['offer-perf-daily', from, to, countries, mode, selectedId, dateBasis],
    queryFn: () => getTrackerStats({
      from: `${from} 00:00:00`,
      to: `${to} 23:59:59`,
      group_by: 'day',
      countries,
      date_basis: dateBasis,
      ...focusFilter,
    }),
    enabled: performanceView === 'offers',
  });

  const appGeoRangeDays = Math.floor(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / (24 * 60 * 60 * 1000),
  ) + 1;
  const appGeoRangeValid = appGeoRangeDays > 0 && appGeoRangeDays <= 92;
  const {
    data: allAppGeoRows = [],
    isLoading: appGeoLoading,
    error: appGeoError,
  } = useQuery({
    queryKey: ['offer-perf-app-geo', from, to, countries],
    queryFn: () => getAppGeoPerfRows({ from, to, countries }),
    enabled: performanceView === 'apps' && appGeoRangeValid,
  });

  const appCoveredGeos = useMemo(() => {
    const set = new Set(coveredGeos);
    for (const row of allAppGeoRows) {
      if (row.country !== '??') set.add(row.country);
    }
    return Array.from(set).sort((a, b) =>
      (COUNTRY_MAP[a]?.name || a).localeCompare(COUNTRY_MAP[b]?.name || b));
  }, [allAppGeoRows, coveredGeos]);

  const appGeoCohortCounts = useMemo(() => {
    const appIds = { new: new Set<string>(), old: new Set<string>() };
    for (const row of allAppGeoRows) appIds[appCohort(row, to, newAppWindowDays)].add(row.appId);
    return {
      new: appIds.new.size,
      old: appIds.old.size,
      all: new Set([...appIds.new, ...appIds.old]).size,
    };
  }, [allAppGeoRows, to, newAppWindowDays]);

  const appGeoRows = useMemo(() => {
    const needle = appSearch.trim().toLowerCase();
    const cohortRows = allAppGeoRows
      .filter((row) => appCohortMode === 'all' || appCohort(row, to, newAppWindowDays) === appCohortMode)
      .filter((row) => !needle
        || row.appName.toLowerCase().includes(needle)
        || row.bundleId.toLowerCase().includes(needle));
    const displayedRows = groupAppGeos ? groupAppGeoRowsByApp(cohortRows) : cohortRows.slice();
    return displayedRows.sort((a, b) => appGeoSortValue(b, appGeoSort) - appGeoSortValue(a, appGeoSort)
      || b.deps - a.deps || b.regs - a.regs || b.clicks - a.clicks);
  }, [allAppGeoRows, appCohortMode, appGeoSort, appSearch, groupAppGeos, newAppWindowDays, to]);

  const ratioSeries = useMemo(() => {
    return dailyRows
      .filter((r) => r.group_value)
      .slice()
      .sort((a, b) => (a.group_value! < b.group_value! ? -1 : 1))
      .map((r) => ({
        date: r.group_value!,
        // deps/regs as a percentage; null on days with no regs so the line
        // shows a gap instead of a misleading 0%.
        ratio: r.regs > 0 ? Math.round((r.deps / r.regs) * 1000) / 10 : null,
        regs: r.regs,
        deps: r.deps,
      }));
  }, [dailyRows]);

  const hasRatioData = ratioSeries.some((p) => p.ratio !== null);

  const labelOf = (id: string | null): { primary: string; secondary?: string; muted?: boolean } => {
    if (!id) return { primary: 'No offer matched', secondary: 'fallback / no flow', muted: true };
    if (tableGroup === 'partner') return { primary: id };
    const o = offerById.get(id);
    if (o) {
      const brandLang = [o.brand, o.lang].filter(Boolean).join(' · ');
      return { primary: o.partner, secondary: brandLang ? `${o.geo} · ${brandLang}` : o.geo };
    }
    return { primary: `(deleted) ${id.slice(0, 8)}…`, muted: true };
  };

  const totals = rows.reduce((acc, r) => ({
    clicks: acc.clicks + r.clicks,
    unique_clicks: acc.unique_clicks + (r.unique_clicks || 0),
    regs: acc.regs + r.regs,
    deps: acc.deps + r.deps,
    payout_cents: acc.payout_cents + r.payout_cents,
  }), { clicks: 0, unique_clicks: 0, regs: 0, deps: 0, payout_cents: 0 });

  return (
    <div>
      {/* Apps performance reads our per-app report; a client has its offers only. */}
      {!isClient && <div className="mb-5 inline-flex overflow-hidden rounded-md border border-border text-sm">
        <button
          type="button"
          onClick={() => setPerformanceView('offers')}
          className={`px-4 py-2 ${performanceView === 'offers' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
        >Offers performance</button>
        <button
          type="button"
          onClick={() => setPerformanceView('apps')}
          className={`border-l border-border px-4 py-2 ${performanceView === 'apps' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
        >Apps performance</button>
      </div>}

      {performanceView === 'offers' ? (
        <>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Offer Performance</h1>
          <p className="text-sm text-content-subtle mt-1">
            Click → reg → dep — only postbacks matched to a tracker click are counted here.
          </p>
          <p className="text-xs text-content-subtle mt-1">
            {dateBasis === 'click'
              ? 'Dates follow the click: a deposit counts on the day its click happened, so recent days keep filling in.'
              : 'Dates follow the postback: regs/deps/payout count on the day they arrived, clicks stay on their own date.'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => { setMode('offer'); setSelectedId(''); }}
              className={`px-3 py-1.5 ${mode === 'offer' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >By offer</button>
            <button
              type="button"
              onClick={() => { setMode('partner'); setSelectedId(''); }}
              className={`px-3 py-1.5 border-l border-border ${mode === 'partner' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >By advertiser</button>
            <button
              type="button"
              onClick={() => { setMode('manager'); setSelectedId(''); }}
              title="Offers grouped by the person who created them"
              className={`px-3 py-1.5 border-l border-border ${mode === 'manager' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >By manager</button>
          </div>
          <div className="min-w-[220px]">
            <label className="block text-xs text-content-subtle mb-1">{mode === 'partner' ? 'Advertiser' : mode === 'manager' ? 'Manager' : 'Offer'}</label>
            <Select
              value={selectedId}
              options={focusOptions}
              onChange={setSelectedId}
              searchable
              size="sm"
              placeholder={mode === 'partner' ? 'All advertisers' : mode === 'manager' ? 'All managers' : 'All offers'}
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Dates by</label>
            <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
              <button
                type="button"
                onClick={() => setDateBasis('click')}
                title="Click cohort: every reg/dep counts on the date of its click, whenever it arrived"
                className={`px-3 py-1.5 ${dateBasis === 'click' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
              >Click date</button>
              <button
                type="button"
                onClick={() => setDateBasis('conversion')}
                title="Regs/deps/payout count on the day the postback arrived; clicks stay on their own date"
                className={`px-3 py-1.5 border-l border-border ${dateBasis === 'conversion' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
              >Conversion date</button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-1.5 border border-border rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-1.5 border border-border rounded-md text-sm" />
          </div>
          <div className="min-w-[200px]">
            <label className="block text-xs text-content-subtle mb-1">GEO</label>
            <CountryMultiSelect
              value={countries}
              onChange={setCountries}
              restrictTo={coveredGeos}
              size="sm"
              placeholder="All countries"
              emptyHint="All countries"
            />
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-md border border-border bg-surface-muted/40 p-3">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-medium text-content truncate">
            Deposit / registration ratio over time
            {selectedId && mode !== 'manager' && (
              <span className="text-content-subtle font-normal"> · {focusOptions.find((o) => o.value === selectedId)?.label}</span>
            )}
            {mode === 'manager' && <span className="text-content-subtle font-normal"> · all offers</span>}
          </h3>
          <span className="text-xs text-content-subtle shrink-0">Reg→Dep %, by day</span>
        </div>
        {dailyLoading ? (
          <div className="flex h-56 items-center justify-center text-sm text-content-subtle">Loading…</div>
        ) : !hasRatioData ? (
          <div className="flex h-56 items-center justify-center text-sm text-content-subtle">No registrations in selected period</div>
        ) : (
          <DepRegRatioChart data={ratioSeries} />
        )}
      </div>

      {mode === 'manager' && !selectedId ? (
        <ManagerTable
          managers={managerStats.managers}
          noOffer={managerStats.noOffer}
          total={managerStats.total}
          isLoading={isLoading}
          onSelect={setSelectedId}
        />
      ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-muted">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-content-subtle uppercase">{tableGroup === 'partner' ? 'Advertiser' : 'Offer'}</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">Clicks</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase" title="Unique clicks (per uniqueness window)">Unique</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">Regs (CR)</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">Deps (CR)</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">Reg→Dep</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">Payout</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-content-subtle uppercase">EPC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {isLoading && (
              <tr><td colSpan={8} className="text-center py-6 text-content-subtle">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-content-subtle">No data for selected period</td></tr>
            )}
            {rows.map((r, i) => {
              const lab = labelOf(r.group_value);
              const offer = tableGroup === 'offer' && r.group_value
                ? offerById.get(r.group_value)
                : undefined;
              return (
                <tr
                  key={i}
                  className={offer?.status === 'active' ? '!bg-success-soft hover:!bg-success-soft' : ''}
                >
                  <td className="px-3 py-2">
                    <div className={`font-medium ${lab.muted ? 'text-content-subtle italic' : 'text-content'}`}>{lab.primary}</div>
                    {lab.secondary && <div className="text-xs text-content-subtle">{lab.secondary}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{r.clicks}</td>
                  <td className="px-3 py-2 text-right">{r.unique_clicks ?? '-'}</td>
                  <td className="px-3 py-2 text-right">{r.regs} <span className="text-xs text-content-subtle">({pct(r.regs, r.clicks)})</span></td>
                  <td className="px-3 py-2 text-right">{r.deps} <span className="text-xs text-content-subtle">({pct(r.deps, r.clicks)})</span></td>
                  <td className="px-3 py-2 text-right text-xs text-content-muted">{pct(r.deps, r.regs)}</td>
                  <td className="px-3 py-2 text-right">{fmtCents(r.payout_cents)}</td>
                  <td className="px-3 py-2 text-right">{r.clicks ? `$${(r.payout_cents / 100 / r.clicks).toFixed(3)}` : '-'}</td>
                </tr>
              );
            })}
            {!isLoading && rows.length > 0 && (
              <tr className="bg-surface-muted font-medium">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{totals.clicks}</td>
                <td className="px-3 py-2 text-right">{totals.unique_clicks}</td>
                <td className="px-3 py-2 text-right">{totals.regs} <span className="text-xs text-content-subtle">({pct(totals.regs, totals.clicks)})</span></td>
                <td className="px-3 py-2 text-right">{totals.deps} <span className="text-xs text-content-subtle">({pct(totals.deps, totals.clicks)})</span></td>
                <td className="px-3 py-2 text-right text-xs text-content-muted">{pct(totals.deps, totals.regs)}</td>
                <td className="px-3 py-2 text-right">{fmtCents(totals.payout_cents)}</td>
                <td className="px-3 py-2 text-right">{totals.clicks ? `$${(totals.payout_cents / 100 / totals.clicks).toFixed(3)}` : '-'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
        </>
      ) : (
      <section>
        <div className="mb-4 flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
          <div>
            <h1 className="text-xl font-semibold text-content sm:text-2xl">Apps Performance</h1>
            <p className="mt-1 max-w-3xl text-sm text-content-subtle">
              App-level performance for the selected dates and GEOs. New means released in the last {newAppWindowDays} days
              as of {to}; when release date is missing, creation date is used. This rollup always follows click date and includes all offers.
            </p>
            <p className="mt-1 text-xs text-content-subtle">
              Payout and EPC are profitability proxies. True ROI is not shown because tracker data has no traffic spend.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs text-content-subtle">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-border px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-content-subtle">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-border px-3 py-1.5 text-sm" />
            </div>
            <div className="min-w-[200px]">
              <label className="mb-1 block text-xs text-content-subtle">GEO</label>
              <CountryMultiSelect
                value={countries}
                onChange={setCountries}
                restrictTo={appCoveredGeos}
                size="sm"
                placeholder="All countries"
                emptyHint="All countries"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-content-subtle">New app window</label>
              <Select
                value={String(newAppWindowDays)}
                options={[
                  { value: '7', label: '7 days' },
                  { value: '14', label: '14 days' },
                  { value: '30', label: '30 days' },
                  { value: '60', label: '60 days' },
                  { value: '90', label: '90 days' },
                ]}
                onChange={(value) => setNewAppWindowDays(Number(value))}
                size="sm"
              />
            </div>
            <div className="min-w-[180px]">
              <label className="mb-1 block text-xs text-content-subtle">Sort by</label>
              <Select
                value={appGeoSort}
                options={APP_GEO_SORT_OPTIONS}
                onChange={(value) => setAppGeoSort(value as AppGeoSort)}
                size="sm"
              />
            </div>
            <label className="flex items-center gap-2 pb-1.5 text-sm text-content-muted">
              <input
                type="checkbox"
                checked={groupAppGeos}
                onChange={(e) => setGroupAppGeos(e.target.checked)}
              />
              Combine all GEOs per app
            </label>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
            <button
              type="button"
              onClick={() => setAppCohortMode('new')}
              className={`px-3 py-1.5 ${appCohortMode === 'new' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >New apps ({appGeoCohortCounts.new})</button>
            <button
              type="button"
              onClick={() => setAppCohortMode('old')}
              className={`border-l border-border px-3 py-1.5 ${appCohortMode === 'old' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >Old apps ({appGeoCohortCounts.old})</button>
            <button
              type="button"
              onClick={() => setAppCohortMode('all')}
              className={`border-l border-border px-3 py-1.5 ${appCohortMode === 'all' ? 'bg-content text-surface' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
            >All apps ({appGeoCohortCounts.all})</button>
          </div>
          <div className="relative">
            <input
              type="search"
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
              placeholder="Search app name or bundle id"
              className="w-64 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content placeholder:text-content-subtle"
            />
          </div>
          <span className="text-xs text-content-subtle">
            Cutoff: {appCohortCutoff(to, newAppWindowDays)} · {appGeoRows.length} {groupAppGeos ? 'app rows' : 'app × GEO rows'}
            {appSearch.trim() && ' · filtered'}
          </span>
        </div>

        {!appGeoRangeValid ? (
          <div className="rounded-md border border-border bg-surface-muted/40 p-4 text-sm text-content-subtle">
            App × GEO export supports date ranges up to 92 days. Shorten the selected period to load it.
          </div>
        ) : appGeoError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            {(appGeoError as Error).message}
          </div>
        ) : (
          <AppGeoTable rows={appGeoRows} isLoading={appGeoLoading} />
        )}
      </section>
      )}
    </div>
  );
}
